import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, User, MapPin, Phone } from "lucide-react";

const COMUNAS_CHILE = [
  "Santiago", "Providencia", "Ñuñoa", "Las Condes", "Vitacura", "Lo Barnechea",
  "La Reina", "Peñalolén", "Macul", "San Miguel", "La Florida", "Puente Alto",
  "Maipú", "Estación Central", "Quinta Normal", "Independencia", "Recoleta",
  "Huechuraba", "Conchalí", "Renca", "Cerrillos", "Pedro Aguirre Cerda",
];

export default function NuevoPedido() {
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    destinatario: { nombre: "", direccion: "", comuna: "", telefono: "", email: "", referencia: "" },
    descripcion: "",
    valor_declarado: "",
  });

  const set = (k, v) => setF({ ...f, [k]: v });
  const setD = (k, v) => setF({ ...f, destinatario: { ...f.destinatario, [k]: v } });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      if (payload.valor_declarado === "") delete payload.valor_declarado;
      else payload.valor_declarado = parseFloat(payload.valor_declarado);
      const r = await api.post("/orders", payload);
      toast.success(`Pedido creado: ${r.data.tracking_number}`);
      nav("/cliente/pedidos");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Nuevo</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Cargar pedido</h1>
      </div>

      <form onSubmit={submit} className="max-w-3xl">
        <div className="border border-white/10 bg-zinc-900/40 mb-6">
          <div className="p-5 border-b border-white/10 flex items-center gap-2">
            <User className="w-5 h-5 text-yellow-300" />
            <div className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400">Destinatario</div>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Nombre completo *</label>
              <input required value={f.destinatario.nombre} onChange={(e) => setD("nombre", e.target.value)} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm" data-testid="dest-nombre" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Dirección (calle + número) *</label>
              <input required value={f.destinatario.direccion} onChange={(e) => setD("direccion", e.target.value)} placeholder="Ej: Av. Providencia 1234, Depto 501" className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm" data-testid="dest-direccion" />
            </div>
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Comuna *</label>
              <input required value={f.destinatario.comuna} onChange={(e) => setD("comuna", e.target.value)} placeholder="Providencia" list="comunas" className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm" data-testid="dest-comuna" />
              <datalist id="comunas">
                {COMUNAS_CHILE.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Teléfono</label>
              <input value={f.destinatario.telefono} onChange={(e) => setD("telefono", e.target.value)} placeholder="+56 9 XXXX XXXX" className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm font-mono-tech" data-testid="dest-telefono" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Email</label>
              <input type="email" value={f.destinatario.email} onChange={(e) => setD("email", e.target.value)} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm font-mono-tech" data-testid="dest-email" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Referencia (opcional)</label>
              <input value={f.destinatario.referencia} onChange={(e) => setD("referencia", e.target.value)} placeholder="Cerca de la plaza, timbre azul, etc." className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm" data-testid="dest-ref" />
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-zinc-900/40 mb-6">
          <div className="p-5 border-b border-white/10 flex items-center gap-2">
            <Package className="w-5 h-5 text-yellow-300" />
            <div className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400">Detalle del envío</div>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Descripción del contenido</label>
              <textarea value={f.descripcion} onChange={(e) => set("descripcion", e.target.value)} rows={3} placeholder="Ej: Ropa, accesorios..." className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm" data-testid="ped-descripcion" />
            </div>
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mb-1 block">Valor declarado (CLP)</label>
              <input type="number" value={f.valor_declarado} onChange={(e) => set("valor_declarado", e.target.value)} className="w-full bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm font-mono-tech" data-testid="ped-valor" />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="bg-yellow-300 text-black font-bold px-8 py-4 uppercase tracking-widest text-sm hover:bg-yellow-200 disabled:opacity-50" data-testid="submit-nuevo-pedido">
            {saving ? "Guardando..." : "Crear pedido"}
          </button>
          <button type="button" onClick={() => nav("/cliente")} className="border border-white/20 hover:border-white/40 px-8 py-4 uppercase tracking-widest text-sm">Cancelar</button>
        </div>
      </form>
    </Layout>
  );
}
